import { useTranslation } from '@aimsos/i18n';
import { Button, IconHighlight, ModalLayout } from '@aimsos/ui';

import { useModalEscape } from '../hooks/useModalEscape.js';
import { ModalFooter } from './parts';

import { Save } from 'lucide-react';

/**
 * `Save to catalog?` (CT-5200), reshaped 2026-09-08.
 *
 * The new prototype folds what used to be two dialogs into one: the confirm now
 * carries the three destinations as well, so the user says in a single step both
 * *save it* and *where to go next*.
 *
 *   - `Create new widget` — save, then reset the builder for another one.
 *   - `Keep editing` — save and stay on this widget.
 *   - `Save and close` — save and go to the library (CT-5306: the prototype's copy).
 *
 * All three save (decision 5 — the label must say so; never a bare `Close`), and
 * per decision 9 the body says *added to the widget library*, never "published":
 * _"Guardarlo no es publicarlo, es solo guardarlo"_.
 */
function SaveToCatalogModal({
  isOpen,
  widgetName,
  isSaving,
  onCancel,
  onSaveAndClose,
  onSaveAndNew,
  onSaveAndKeepEditing,
}) {
  const { t } = useTranslation('dashboards');

  useModalEscape(isOpen, onCancel, { disabled: isSaving });
  if (!isOpen) return null;

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={onCancel}
      useBodyPortal
      maxWidthClassName="max-w-lg"
      titlePrefix={<IconHighlight icon={Save} bgColor="Primary" iconColor="PrimaryAlt" size="medium" />}
      title={t('widgetBuilder.saveModal.title')}
      closeDisabled={isSaving}
    >
      <div className="px-6 py-4">
        <p className="m-0 text-sm text-content-secondary">
          {t('widgetBuilder.saveModal.body', { name: widgetName })}
        </p>
      </div>

      <ModalFooter
        cancelLabel={t('widgetBuilder.saveModal.createNew')}
        onCancel={onSaveAndNew}
        submitting={isSaving}
        secondaryAction={
          <Button
            type="button"
            variant="secondary"
            onClick={onSaveAndKeepEditing}
            className="!text-[12px]"
          >
            {t('widgetBuilder.saveModal.keepEditing')}
          </Button>
        }
        primaryAction={
          <Button variant="primary" onClick={onSaveAndClose} className="!text-[12px]">
            {t('widgetBuilder.saveModal.confirm')}
          </Button>
        }
      />
    </ModalLayout>
  );
}

export default SaveToCatalogModal;
